import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { BasicCellType, TableCellType } from "components/LazyTable";
import useBoolean from "hooks/useBoolean";
import UserSkusTable from "pages/UserListPage/UserSkusTable";
import { useEffect } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { OrganizationUserDetails } from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";

type ProcessChargebackDialogProps = {
  open: boolean;
  onClose: () => void;
  submitting: boolean;
  saveButtonEnabled: boolean | undefined;
  parentUserSkuId: string;
  user: OrganizationUserDetails;
  completedDateFrom?: Date;
};

export const ProcessChargebackDialog = ({
  open,
  submitting,
  saveButtonEnabled,
  onClose,
  parentUserSkuId,
  user,
}: ProcessChargebackDialogProps) => {
  const { formatMessage: t } = useIntl();
  const [emptyChildSkus, setIsEmptyChildSkus] = useBoolean(false);

  const chargebackRenewalDate = persistanceStore.useState(
    (s) => s.userSkusFilterChargebackRenewalDate
  );

  useEffect(() => {
    setIsEmptyChildSkus.setFalse();
  }, [setIsEmptyChildSkus, chargebackRenewalDate]);

  const basicCell = {
    align: "left",
    width: 0,
    type: "text",
  } as BasicCellType;

  const cells: Array<TableCellType> = [
    {
      ...basicCell,
      accessor: "sku",
      label: t({ id: "skuLabel" }),
    },
    {
      ...basicCell,
      accessor: "title",
      label: t({ id: "skuTitleLabel" }),
    },
    {
      ...basicCell,
      accessor: "format",
      label: t({ id: "formatLabel" }),
    },
    {
      ...basicCell,
      accessor: "startDate",
      type: "date",
      label: t({ id: "startDateLabel" }),
    },
    {
      ...basicCell,
      accessor: "endDate",
      type: "date",
      label: t({ id: "endDateLabel" }),
    },
    {
      ...basicCell,
      accessor: "completedDate",
      type: "date",
      label: t({ id: "completionDateLabel" }),
    },
    {
      ...basicCell,
      accessor: "userSkuStateName",
      customSortAccessor: "userSkuState.name",
      label: t({ id: "statusLabel" }),
    },
    {
      ...basicCell,
      accessor: "isListedInCatalog",
      type: "boolean",
      label: t({ id: "listedLabel" }),
    },
  ];

  return (
    <Dialog
      disableEnforceFocus
      maxWidth="lg"
      fullWidth
      open={open}
      onClose={onClose}
      disablePortal
    >
      <DialogTitle>
        <T id="areYouSure" />
      </DialogTitle>
      <DialogContent>
        <Box my={2}>
          <Alert severity="warning">
            <Typography>
              <T
                id="chargeBackUserSkuConfirmation"
                values={{
                  linebreak: (
                    <>
                      <br />
                      <br />
                    </>
                  ),
                }}
              />
            </Typography>
          </Alert>
        </Box>
        {user && !emptyChildSkus && (
          <UserSkusTable
            {...{
              userId: user?.id,
              parentUserSkuId,
              title: t({ id: "childSkusAffectedLabel" }),
              onEmptyChildSkus: () => setIsEmptyChildSkus.setTrue(),
              cells,
              ...user,
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          <T id="closeButton" />
        </Button>
        <Button
          variant="contained"
          type="submit"
          color="primary"
          disabled={submitting || !saveButtonEnabled}
        >
          {submitting && <CircularProgress size={16} sx={{ mr: 1 }} />}
          <T id="processButton" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
